import { Link, useParams } from 'react-router-dom';
import { tasksApi } from 'features/tasks/api';
import { Box, Button, Chip, Container, Divider, IconButton, Paper, Skeleton, Stack, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import { useState } from 'react';
import ManagerSpan from 'shared/ui/manager-span';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { getTaskStatusMeta } from 'features/tasks/task-status-meta';
import { TaskForm } from 'features/tasks/task-form';
import { useEditTaskForm } from 'features/tasks/use-edit-task-form';
import { TaskDto } from '@sr/dto';

export const TaskDetailsPage = () => {
  const { taskId } = useParams();
  const id = Number(taskId);
  const { data: task, isLoading, refetch } = tasksApi.useGetTaskQuery(id, { skip: Number.isNaN(id) });

  if (isLoading || !task) {
    return (
      <Container maxWidth="lg">
        <Skeleton variant="text" height={48} />
        <Skeleton variant="rectangular" height={200} />
      </Container>
    );
  }

  return <TaskDetailsContent task={task} refetch={() => refetch()} />;
};

export default TaskDetailsPage;

const formatDate = (d?: string | Date | null) => {
  if (!d) {
    return '-';
  }
  const date = typeof d === 'string' ? new Date(d) : d;
  return format(date, 'dd.MM.yyyy', { locale: ru });
};

const TaskDetailsContent = ({ task, refetch }: { task: TaskDto; refetch: () => void }) => {
  const [editing, setEditing] = useState(false);
  const statusMeta = getTaskStatusMeta(task.status);

  const { initialValues, handleSubmit } = useEditTaskForm(task, () => {
    refetch();
    setEditing(false);
  });

  const handleCancel = () => {
    refetch();
    setEditing(false);
  };

  return (
    <Container maxWidth="lg">
      <Stack spacing={3}>
        <Button startIcon={<ArrowBackIcon />} component={Link} to="/events/tasks" color="inherit" sx={{ alignSelf: 'flex-start' }}>
          К списку задач
        </Button>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="h4">Задача #{task.id}</Typography>
          <Chip size="small" label={statusMeta.label} color={statusMeta.chipColor} variant="outlined" />
          {!editing && (
            <IconButton size="small" onClick={() => setEditing(true)}>
              <EditIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>

        <Paper sx={{ p: 3 }}>
          {editing ? (
            <TaskForm initialValues={initialValues} onSubmit={handleSubmit} onCancel={handleCancel} />
          ) : (
            <Stack spacing={3}>
              <Box>
                <Typography variant="overline" color="text.secondary">
                  Тема
                </Typography>
                <Typography variant="h6">{task.theme || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="overline" color="text.secondary">
                  Описание
                </Typography>
                <Typography sx={{ whiteSpace: 'pre-wrap' }}>{task.description || '-'}</Typography>
              </Box>
              <Divider />
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                <Box sx={{ minWidth: 260 }}>
                  <Typography variant="overline" color="text.secondary">
                    Менеджер
                  </Typography>
                  {task.manager ? <ManagerSpan person={task.manager} /> : <Typography color="text.secondary">-</Typography>}
                </Box>
                <Box sx={{ minWidth: 260 }}>
                  <Typography variant="overline" color="text.secondary">Сотрудники</Typography>
                  {task.employees && task.employees.length > 0 ? (
                    <Stack spacing={0.5}>
                      {task.employees.map((emp) => (
                        <ManagerSpan key={emp.id} person={emp} />
                      ))}
                    </Stack>
                  ) : (
                    <Typography color="text.secondary">-</Typography>
                  )}
                </Box>
                <Box sx={{ minWidth: 200 }}>
                  <Typography variant="overline" color="text.secondary">
                    Срок
                  </Typography>
                  <Typography>{formatDate(task.dueDate ?? null)}</Typography>
                </Box>
              </Stack>
            </Stack>
          )}
        </Paper>
      </Stack>
    </Container>
  );
};
